/**
 * Allys Navigation
 * 
 * Theme-specific navigation for Allys Real Estate.
 * Includes logo, navigation items, currency switcher, and language switcher.
 * Supports multi-language through messages system.
 */

'use client';

import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { CoreNavigation, type NavigationItem } from '@/components/navigation/CoreNavigation';
import type { NavigationProps } from '@routeroyal/theme-core';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useMemo } from 'react';
import { CurrencySwitcher } from './CurrencySwitcher';
import { AllysLocaleSwitcher } from './LocaleSwitcher';

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Navigation Component
 * 
 * Custom navigation for Allys theme with real estate specific features
 */
export const AllysNavigation = memo(function AllysNavigation({ 
  logo, 
  links, 
  ctaText: _ctaText, 
  ctaLink: _ctaLink, 
  locale = 'en',
  messages,
  hierarchyLinks 
}: NavigationProps) {
  // Build navigation items - PRIORITY: links prop > messages > fallback
  // Links prop takes priority because it may contain tenant-specific paths (/site/{orgId}/...)
  const navigationItems = useMemo<NavigationItem[]>(() => {
    let items: NavigationItem[] = [];
    
    // FIRST: Use links if provided (these may be tenant-specific paths)
    if (links && links.length > 0) {
      const nav = messages?.navigation as Record<string, unknown> | undefined;
      
      // Map of href path segments to translation keys for reliable matching
      const hrefToTranslationKey: Record<string, string> = {
        'properties': 'properties',
        'projects': 'projects',
        'news': 'news',
        'about': 'about',
        'contact': 'contact',
        'resources': 'resources',
        'spaces': 'spaces',
      };
      
      items = links
        .filter(link => link.label.toLowerCase() !== 'allys')
        .map(link => {
          let translatedLabel = link.label;
          if (nav) {
            // Match by href path (last meaningful segment) rather than label text
            const pathSegment = link.href.replace(/\/$/, '').split('/').pop() || '';
            const translationKey = hrefToTranslationKey[pathSegment];
            
            if (translationKey && nav[translationKey]) {
              translatedLabel = safeGetString(nav[translationKey], link.label);
            } else if (pathSegment === '' || link.href.match(/^\/[a-z]{2}\/?$/)) {
              // Home page link
              translatedLabel = safeGetString(nav.home, link.label);
            }
          }
          
          const item: NavigationItem = {
            name: translatedLabel,
            href: link.href,
            hasDropdown: link.hasDropdown ?? false,
            ...(link.isExternal !== undefined && { isExternal: link.isExternal }),
          };

          if (link.hasDropdown && link.dropdownItems) {
            item.dropdownItems = link.dropdownItems.map(di => ({
              name: di.label,
              href: di.href,
              ...(di.description !== undefined && { description: di.description }),
              ...(di.children && {
                children: di.children.map(child => ({
                  name: child.label,
                  href: child.href,
                  ...(child.description !== undefined && { description: child.description }),
                })),
              }),
            }));
          }
          
          return item;
        });
    }
    // SECOND: Build from messages with locale prefix (for marketing site)
    else if (messages) {
      const nav = messages.navigation as Record<string, unknown> | undefined;
      
      items = [
        { name: safeGetString(nav?.home, 'Home'), href: `/${locale}`, hasDropdown: false },
        { name: safeGetString(nav?.properties, 'Properties'), href: `/${locale}/properties`, hasDropdown: false },
        { name: safeGetString(nav?.projects, 'Projects'), href: `/${locale}/projects`, hasDropdown: false },
        { name: safeGetString(nav?.news, 'News'), href: `/${locale}/news`, hasDropdown: false },
        { name: safeGetString(nav?.about, 'About'), href: `/${locale}/about`, hasDropdown: false },
        { name: safeGetString(nav?.contact, 'Contact'), href: `/${locale}/contact`, hasDropdown: false },
      ];
    }
    // THIRD: Default fallback menu (English)
    else {
      items = [
        { name: 'Home', href: `/${locale}`, hasDropdown: false },
        { name: 'Properties', href: `/${locale}/properties`, hasDropdown: false },
        { name: 'Projects', href: `/${locale}/projects`, hasDropdown: false },
        { name: 'News', href: `/${locale}/news`, hasDropdown: false },
        { name: 'About', href: `/${locale}/about`, hasDropdown: false },
        { name: 'Contact', href: `/${locale}/contact`, hasDropdown: false },
      ];
    }
    
    // Filter out any items with "Allys" in the name (case-insensitive)
    return items.filter(item => item.name.toLowerCase() !== 'allys');
  }, [links, messages, locale]);

  // Convert logo string to ReactNode
  const logoNode = useMemo(() => {
    const homeHref = locale ? `/${locale}` : '/';
    
    // Only render Image if logo is a non-empty string
    if (typeof logo === 'string' && logo.length > 0) {
      return (
        <Link href={homeHref} className="block -ml-2">
          <Image
            src={logo}
            alt="Home"
            width={160}
            height={40}
            className="h-8 md:h-10 w-auto transition-all duration-300 hover:scale-105 drop-shadow-sm"
            priority
            unoptimized
          />
        </Link>
      );
    }
    // If logo is a ReactNode (or empty string), return as-is or fallback to text
    if (logo && typeof logo !== 'string') {
      return logo;
    }
    // Fallback: text-based logo when no image is provided
    return (
      <Link href={homeHref} className="block -ml-2">
        <span className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
          Allys
        </span>
      </Link>
    );
  }, [logo, locale]);

  const { isAuthenticated } = useCustomerAuth();

  // CTA label and href depend on authentication state
  const nav = messages?.navigation as Record<string, unknown> | undefined;
  const ctaLabel = isAuthenticated
    ? safeGetString(nav?.myAccount, 'My Account')
    : safeGetString(nav?.getStarted, 'Get Started');
  const ctaHref = isAuthenticated ? `/${locale}/account` : `/${locale}/login`;

  // Build actions (CTA button + currency switcher + locale switcher)
  const actions = useMemo(() => {
    return (
      <div className="flex items-center gap-2">
        {/* Currency Switcher */}
        <CurrencySwitcher />
        
        {/* Language Switcher - ONLY French and English */}
        <AllysLocaleSwitcher locale={locale} variant="pill" />
        
        {/* CTA Button - Pill Style */}
        <Link
          href={ctaHref}
          className="ml-2 px-4 py-1.5 rounded-full bg-[#243462] text-white text-sm font-medium hover:bg-[#1a2847] active:scale-[0.98] transition-all duration-200 shadow-sm"
        >
          {ctaLabel}
        </Link>
      </div>
    );
  }, [locale, ctaLabel, ctaHref]);

  // Build mobile actions (CTA + currency switcher + locale switcher)
  const mobileActions = useMemo(() => {
    return (
      <div className="space-y-6">
        {/* CTA Button - Mobile Pill Style */}
        <Link
          href={ctaHref}
          className="block w-full px-6 py-4 rounded-full bg-[#243462] text-white text-center font-semibold hover:bg-[#1a2847] active:scale-[0.98] transition-all duration-200 shadow-md min-h-[52px] touch-manipulation"
        >
          {ctaLabel}
        </Link>
        
        {/* Currency and Language Switchers - Mobile optimized with proper spacing */}
        <div className="flex justify-center items-start gap-4 pt-2">
          <CurrencySwitcher inMobileMenu />
          <AllysLocaleSwitcher locale={locale} inMobileMenu />
        </div>
      </div>
    );
  }, [locale, ctaLabel, ctaHref]);

  // Convert hierarchyLinks format from NavigationProps to CoreNavigation format
  const convertedHierarchyLinks = useMemo(() => {
    if (!hierarchyLinks) return undefined;
    return {
      ...(hierarchyLinks.parent && {
        parent: {
          name: hierarchyLinks.parent.label,
          href: hierarchyLinks.parent.href,
        },
      }),
      ...(hierarchyLinks.children && {
        children: hierarchyLinks.children.map(child => ({
          name: child.label,
          href: child.href,
        })),
      }),
    };
  }, [hierarchyLinks]);

  return (
    <CoreNavigation
      logo={logoNode}
      items={navigationItems}
      basePath=""
      actions={actions}
      mobileActions={mobileActions}
      {...(convertedHierarchyLinks && { hierarchyLinks: convertedHierarchyLinks })}
      className=""
      logoClassName=""
      menuClassName=""
      actionsClassName=""
      sticky={true}
      scrollEffect={true}
      enableAnimations={true}
    />
  );
});

